import {
  ActivitySettings,
  ActivityType,
  DiagramGeneralSettings,
  FlowlineSettings,
  ResourceActivityPayload,
  PositionType
} from '../diagram-type-styles.model';
import {ArchitectureBaseConfig} from './diagram-architecture-base-config';
import {FlowlineForm} from '../flowline-form.enum';
import {INTERACTION_FLOW} from '../../../shared/constants/diagram-subtypes.constants';
import {MAPS_TYPES_ENUM as MAP_TYPES} from '../../../editor-interface/shared/models/map.model';

export class InteractionFlowConfig extends ArchitectureBaseConfig {
  generalSettings(): DiagramGeneralSettings {
    return {
      ...super.generalSettings(),
      tagColor: '#D14719',
      allowedDrilldownSubtypes: [MAP_TYPES.INTERACTION_FLOW, MAP_TYPES.DATA_MODEL]
    };
  }

  readonly activityShape = {w: 200, h: 210};

  activitySettings(x?: number, y?: number): ActivitySettings {
    return {
      ...super.activitySettings(x, y),
      text: 'Card Name',
      attributePadding: true,
      footerText: 'Provide short description...',
      defaultAttributes: [
        {mxKey: 1, text: 'Attribute 1'},
        {mxKey: 2, text: 'Attribute 2'},
        {mxKey: 3, text: 'Attribute 3'}
      ]
    };
  }

  flowlineSettings(x?: number, y?: number, id?: number | string): FlowlineSettings {
    const baseSettings = super.flowlineSettings(x, y, id);

    return {
      ...baseSettings,
      form: {
        shape: FlowlineForm.shape.Orthogonal,
        type: FlowlineForm.type.Solid,
        start: FlowlineForm.end.None,
        end: FlowlineForm.end.FullArrow
      }
    };
  }

  readonly activityTypes: ActivityType[] = [
    {
      tooltip: 'ASIDE_L.SYSTEM_DEFAULT_ACTIVITY_TOOLTIP',
      icon: 'activity_icon_v4',
      settings: (x?: number, y?: number) => this.activitySettings(x, y)
    },
    this.getResourceActivityType({
      type: INTERACTION_FLOW.RESOURCE_ACTIVITY_TYPES.START,
      text: 'Start/Trigger',
      footerText:
        'Defines the event that initiates the automation; use it to kick off the logic based on a ' +
        'record change, schedule, or external input.'
    }),
    this.getResourceActivityType({
      type: INTERACTION_FLOW.RESOURCE_ACTIVITY_TYPES.GET_RECORDS,
      text: 'Get Records',
      footerText:
        'Retrieves data from Salesforce based on specified criteria; use it when you need to pull ' +
        'records for further actions or decisions.'
    }),
    this.getResourceActivityType({
      type: INTERACTION_FLOW.RESOURCE_ACTIVITY_TYPES.CREATE_RECORDS,
      text: 'Create Records',
      footerText:
        'Inserts new records into Salesforce; use it when the automation needs to generate new ' +
        'data entries.'
    }),
    this.getResourceActivityType({
      type: INTERACTION_FLOW.RESOURCE_ACTIVITY_TYPES.DELETE_RECORDS,
      text: 'Delete Records',
      footerText:
        'Removes records from the system; use it when specific records are no longer needed and ' +
        'should be purged.'
    }),
    this.getResourceActivityType({
      type: INTERACTION_FLOW.RESOURCE_ACTIVITY_TYPES.UPDATE_RECORDS,
      text: 'Update Records',
      footerText:
        'Modifies existing records with new data; use it to change field values after evaluating ' +
        'or processing information.'
    }),
    this.getResourceActivityType({
      type: INTERACTION_FLOW.RESOURCE_ACTIVITY_TYPES.ROLLBACK_RECORDS,
      text: 'Rollback Records',
      footerText:
        'Reverts records to their previous state; use it to undo changes if an error occurs ' +
        'during the automation process.'
    }),
    this.getResourceActivityType({
      type: INTERACTION_FLOW.RESOURCE_ACTIVITY_TYPES.ASSIGNMENT,
      text: 'Assignment',
      footerText:
        'Assigns a value to a variable; use it to store or update data within the flow, such as ' +
        'setting fields or counters for loop iterations.'
    }),
    this.getResourceActivityType({
      type: INTERACTION_FLOW.RESOURCE_ACTIVITY_TYPES.FILTER,
      text: 'Collection Filter',
      footerText:
        'Refines a collection of records based on conditions; use it when you need to work with ' +
        'a subset of records within a larger collection.'
    }),
    this.getResourceActivityType({
      type: INTERACTION_FLOW.RESOURCE_ACTIVITY_TYPES.SORT,
      text: 'Collection Sort',
      footerText:
        'Organizes records in a specific order; use it when you need to process records in a ' +
        'specific sequence based on a field value.'
    }),
    this.getResourceActivityType({
      type: INTERACTION_FLOW.RESOURCE_ACTIVITY_TYPES.DECISION,
      text: 'Decision',
      footerText:
        'Splits the logic into different paths based on conditions; use it to handle logic that ' +
        'requires different actions depending on different record values.'
    }),
    this.getResourceActivityType({
      type: INTERACTION_FLOW.RESOURCE_ACTIVITY_TYPES.PAUSE,
      text: 'Pause',
      footerText:
        'Temporarily halts the automation until a condition is met; use it for long-running ' +
        'processes that wait for an event or time delay.'
    }),
    this.getResourceActivityType({
      type: INTERACTION_FLOW.RESOURCE_ACTIVITY_TYPES.RECOMMENDATION_ASSIGNMENT,
      text: 'Transform',
      footerText:
        'Maps and restructures data between resources; use it to align and convert source fields into ' +
        'the required target format for updates, subflows, or external systems.'
    }),
    this.getResourceActivityType({
      type: INTERACTION_FLOW.RESOURCE_ACTIVITY_TYPES.LOOP,
      text: 'Loop',
      footerText:
        'Iterates through a collection of records to perform actions on each one; use it when ' +
        'you need to process multiple records individually.'
    }),
    this.getResourceActivityType({
      type: INTERACTION_FLOW.RESOURCE_ACTIVITY_TYPES.WAIT,
      text: 'Wait',
      footerText:
        'Pauses the flow until a specified condition, duration, or date is met; use it to delay ' +
        'actions based on time or record changes.'
    }),
    this.getResourceActivityType({
      type: INTERACTION_FLOW.RESOURCE_ACTIVITY_TYPES.SCREEN,
      text: 'Screen',
      footerText:
        'Presents a form to collect input from users during the flow; use it for interactions ' +
        'requiring real-time user data entry.'
    }),
    this.getResourceActivityType({
      type: INTERACTION_FLOW.RESOURCE_ACTIVITY_TYPES.FLOW,
      text: 'Call Another Flow',
      footerText:
        'Invokes another flow or automation; use it for modular, reusable logic or when ' +
        'dividing complex processes into smaller flows.'
    }),
    this.getResourceActivityType({
      type: INTERACTION_FLOW.RESOURCE_ACTIVITY_TYPES.SEND_EMAIL,
      text: 'Send Email',
      footerText:
        'Sends an email to specified recipients; use it for notifications or confirmations ' +
        'triggered by automation events.'
    }),
    this.getResourceActivityType({
      type: INTERACTION_FLOW.RESOURCE_ACTIVITY_TYPES.ACTION,
      text: 'Action',
      footerText:
        'Executes a specific predefined action, like invoking an Apex class, Quick or Global ' +
        'Action; use it for custom functionality or system integration.'
    }),
    this.getResourceActivityType({
      type: INTERACTION_FLOW.RESOURCE_ACTIVITY_TYPES.CUSTOM_ERROR,
      text: 'Custom Error',
      footerText:
        'Displays a targeted error message and halts the flow; use it to prevent record changes ' +
        'and guide users when inputs or conditions are invalid.'
    }),
    this.getResourceActivityType({
      type: INTERACTION_FLOW.RESOURCE_ACTIVITY_TYPES.ADD_PROMPT_INSTRUCTIONS,
      text: 'Add Prompt Instructions',
      footerText:
        'Supplies dynamic input to a Prompt Builder template; use it to merge flow data into ' +
        'AI-generated prompts in template-triggered flows.'
    })
  ];

  protected getDefaultResources(keys: string[]) {
    return super.getDefaultResources(keys).map(resource => {
      return {
        ...resource,
        main: true
      };
    });
  }

  private getResourceActivityType({type, text, footerText}: ResourceActivityPayload) {
    const {icon} = this.diagramMeta?.defaultResources?.[type] || {};

    return {
      type,
      tooltip: text,
      img: icon,
      settings: (x?: number, y?: number) => ({
        ...this.resourceActivitySettings(x, y),
        text,
        footerText,
        defaultResources: this.getDefaultResources([type])
      })
    };
  }

  private resourceActivitySettings(x?: number, y?: number): ActivitySettings {
    const resourceActivityShape = {w: 200, h: 256};

    return {
      ...this.activitySettings(x, y),
      defaultActivityShape: resourceActivityShape,
      position: this.getPosition(resourceActivityShape, x, y, PositionType.CENTER),
      dragHandlePosition: this.getPosition(resourceActivityShape, x, y, PositionType.DEFAULT)
    };
  }
}
